import React from 'react';
import { formatDateInBusinessTZ } from '../../utils/date';

export interface PrintableCouponData {
  code: string;
  amount: number;
  title: string;
  expiresAt: string | null; // null means no expiration
  colorIndex: number; // 0-3 for the 4 color variations
}

// Color variations matching the design (cream, yellow, teal, coral)
const COUPON_COLORS = [
  { bg: '#FEF3E2', border: '#D4A574' }, // Cream/beige
  { bg: '#FFDD44', border: '#E8C244' }, // Yellow
  { bg: '#6ECFC3', border: '#5ABFB3' }, // Teal
  { bg: '#F7A5A5', border: '#E88B8B' }, // Coral/pink
];

interface PrintableCouponProps {
  data: PrintableCouponData;
  forPrint?: boolean;
}

export const PrintableCoupon: React.FC<PrintableCouponProps> = ({ data, forPrint = false }) => {
  const color = COUPON_COLORS[data.colorIndex % 4];
  const scale = forPrint ? 1 : 0.85;

  // Font size helper - uses pt for print, scaled pt for preview
  const fontSize = (pt: number) => forPrint ? `${pt}pt` : `${pt * scale}pt`;

  // Format expiration date using business timezone
  const formatExpiry = (dateStr: string | null): string => {
    if (!dateStr) return 'No Expiration';
    // Use timezone-aware formatting, then convert to 2-digit year format
    const formatted = formatDateInBusinessTZ(dateStr);
    // formatDateInBusinessTZ returns MM/DD/YYYY, convert to MM/DD/YY
    const [month, day, year] = formatted.split('/');
    return `${month}/${day}/${year.slice(-2)}`;
  };

  return (
    <div
      className={`relative ${forPrint ? '' : 'shadow-md'}`}
      style={{
        width: forPrint ? '4in' : `${4 * 96 * scale}px`,
        height: forPrint ? '2.5in' : `${2.5 * 96 * scale}px`,
        backgroundColor: color.bg,
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Dashed border inside */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '6px',
          bottom: '6px',
          left: '6px',
          right: '6px',
          border: `2px dashed ${color.border}`,
        }}
      />

      {/* Content container */}
      <div className="flex h-full" style={{ padding: forPrint ? '12px 28px 16px 28px' : '10px 24px 14px 24px' }}>
        {/* Left section - Logo */}
        <div className="flex flex-col items-center justify-center" style={{ width: forPrint ? '105px' : '84px', minWidth: forPrint ? '105px' : '84px' }}>
          <img
            src="/assets/bhcs-logo-small.png"
            alt="BHCS Logo"
            style={{
              width: forPrint ? '90px' : '72px',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>

        {/* Right section - Content */}
        <div className="flex-1 flex flex-col" style={{ paddingLeft: forPrint ? '10px' : '8px' }}>
          {/* Header */}
          <div>
            <p
              style={{
                fontSize: fontSize(11),
                fontWeight: 500,
                color: '#333',
                marginBottom: '1px',
                fontStyle: 'italic',
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}
            >
              Bright Horizon Chinese School
            </p>
            <h2
              style={{
                fontSize: fontSize(24),
                fontWeight: 900,
                color: '#1a1a1a',
                letterSpacing: '2px',
                margin: '0 0 1px 0',
                fontFamily: 'Georgia, "Times New Roman", serif',
                textTransform: 'uppercase',
              }}
            >
              {data.title}
            </h2>
          </div>

          {/* Description */}
          <p
            style={{
              fontSize: fontSize(9.5),
              color: '#444',
              margin: '0 0 4px 0',
              lineHeight: 1.3,
            }}
          >
            使用此折扣码，可在有效期截止前您选择的任意日期报名BHCS的任何项目，可获得价值<strong>${data.amount}美金的优惠</strong>
          </p>

          {/* Footer - Code and Expiry */}
          <div className="flex flex-col">
            <p
              style={{
                fontSize: fontSize(10.5),
                color: '#333',
                fontWeight: 600,
                margin: '0 0 2px 0',
              }}
            >
              折扣码:{data.code}
            </p>
            <p
              style={{
                fontSize: fontSize(10.5),
                color: '#555',
                margin: 0,
                fontStyle: 'italic',
              }}
            >
              有效期至：{formatExpiry(data.expiresAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Back side of coupon with instructions
export const PrintableCouponBack: React.FC<PrintableCouponProps> = ({ data, forPrint = false }) => {
  const color = COUPON_COLORS[data.colorIndex % 4];
  const scale = forPrint ? 1 : 0.85;

  // Font size helper - uses pt for print, scaled pt for preview
  const fontSize = (pt: number) => forPrint ? `${pt}pt` : `${pt * scale}pt`;

  const instructions = [
    '本礼券可转赠他人使用',
    '扫二维码了解更多详情',
  ];

  return (
    <div
      className={`relative ${forPrint ? '' : 'shadow-md'}`}
      style={{
        width: forPrint ? '4in' : `${4 * 96 * scale}px`,
        height: forPrint ? '2.5in' : `${2.5 * 96 * scale}px`,
        backgroundColor: color.bg,
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Dashed border inside - same as front */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '6px',
          bottom: '6px',
          left: '6px',
          right: '6px',
          border: `2px dashed ${color.border}`,
        }}
      />

      {/* Content container - same padding as front */}
      <div className="flex h-full" style={{ padding: forPrint ? '14px 24px 14px 24px' : '12px 20px 12px 20px' }}>
        {/* Left section - 1/4 width: Instructions + QR */}
        <div
          className="flex flex-col justify-center items-center"
          style={{
            width: '28%',
            borderRight: `1px dashed ${color.border}`,
            paddingRight: forPrint ? '10px' : '8px',
          }}
        >
          <h3
            style={{
              fontSize: fontSize(10),
              fontWeight: 700,
              color: '#1a1a1a',
              marginBottom: forPrint ? '4px' : '3px',
              textAlign: 'center',
              fontFamily: 'Arial, sans-serif',
            }}
          >
            使用说明
          </h3>
          <div className="flex flex-col" style={{ gap: forPrint ? '2px' : '1px', marginBottom: forPrint ? '6px' : '4px' }}>
            {instructions.map((instruction, index) => (
              <p
                key={index}
                style={{
                  fontSize: fontSize(5.5),
                  color: '#444',
                  margin: 0,
                  lineHeight: 1.3,
                  fontFamily: 'Arial, sans-serif',
                  textAlign: 'center',
                }}
              >
                {index + 1}. {instruction}
              </p>
            ))}
          </div>
          <img
            src="/assets/faq-qr-code.svg"
            alt="FAQ QR Code"
            style={{
              width: forPrint ? '54px' : '42px',
              height: forPrint ? '54px' : '42px',
              objectFit: 'contain',
            }}
          />
        </div>

        {/* Right section - 3/4 width: Two ads stacked */}
        <div
          className="flex flex-col"
          style={{
            width: '72%',
            paddingLeft: forPrint ? '10px' : '8px',
          }}
        >
          {/* Top ad - Afterschool Admission */}
          <div
            className="flex items-center"
            style={{
              height: '50%',
              borderBottom: `1px dashed ${color.border}`,
              paddingBottom: forPrint ? '4px' : '3px',
            }}
          >
            <div className="flex-1 flex flex-col justify-center" style={{ padding: forPrint ? '0 6px' : '0 4px', gap: forPrint ? '2px' : '1px' }}>
              <p style={{ fontSize: fontSize(8), fontWeight: 600, color: '#1a1a1a', margin: 0, lineHeight: 1.4 }}>📚 中文课后班</p>
              <p style={{ fontSize: fontSize(6), fontWeight: 600, color: '#444', margin: 0, lineHeight: 1.4 }}>
                2026学年全面招生中
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · TK–G5 多元课程 — 助力孩子多维度成长
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 中文·数学·演讲·国际象棋·绘画·音乐·篮球
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 二月底前报名享早鸟优惠！
              </p>
            </div>
            <img
              src="/assets/school-year-qr-code.svg"
              alt="School Year QR"
              style={{
                width: forPrint ? '54px' : '42px',
                height: forPrint ? '54px' : '42px',
                objectFit: 'contain',
                flexShrink: 0,
                alignSelf: 'center',
              }}
            />
          </div>

          {/* Bottom ad - Spring Break */}
          <div
            className="flex items-center"
            style={{
              height: '50%',
              paddingTop: forPrint ? '4px' : '3px',
            }}
          >
            <div className="flex-1 flex flex-col justify-center" style={{ padding: forPrint ? '0 6px' : '0 4px', gap: forPrint ? '2px' : '1px' }}>
              <p style={{ fontSize: fontSize(8), fontWeight: 600, color: '#1a1a1a', margin: 0, lineHeight: 1.4 }}>
                ⛺️ 春令营
              </p>
              <p style={{ fontSize: fontSize(6), fontWeight: 600, color: '#444', margin: 0, lineHeight: 1.4 }}>
                3月27日–4月6日 9:00am–6:00pm
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 3月27日｜探访亚洲艺术博物馆
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 3月30日–4月1日｜三天经典童子军户外营地
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 4月2日–4月3日｜清明主题文化营
              </p>
              <p style={{ fontSize: fontSize(6), color: '#444', margin: 0, lineHeight: 1.4 }}>
                · 4月6日｜"三国"主题故事营地
              </p>
            </div>
            <img
              src="/assets/spring-break-qr-code.svg"
              alt="Spring Break QR"
              style={{
                width: forPrint ? '54px' : '42px',
                height: forPrint ? '54px' : '42px',
                objectFit: 'contain',
                flexShrink: 0,
                alignSelf: 'center',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for rendering a full page of 8 coupons (2x4 grid) for printing
interface PrintableCouponPageProps {
  coupons: PrintableCouponData[];
  showBack?: boolean;
}

export const PrintableCouponPage: React.FC<PrintableCouponPageProps> = ({ coupons, showBack = false }) => {
  // Ensure we have exactly 8 coupons per page
  const pageCoupons = coupons.slice(0, 8);
  
  return (
    <div
      className="printable-coupon-page"
      style={{
        width: '8.5in',
        height: '11in',
        padding: '0.25in 0.25in',
        backgroundColor: 'white',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 4in)',
        gridTemplateRows: 'repeat(4, 2.5in)',
        gap: '0.125in 0',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      {pageCoupons.map((coupon, index) => (
        showBack ? (
          <PrintableCouponBack key={index} data={coupon} forPrint={true} />
        ) : (
          <PrintableCoupon key={index} data={coupon} forPrint={true} />
        )
      ))}
    </div>
  );
};

export { COUPON_COLORS };
